import { CourseProgress, type Course } from '@/models/Course';
import { type Summary } from '@/models/Summary';
import { MilestoneLogCode, type Milestone } from '@/models/Milestone';
import {
  ActivityMode,
  ContentMode,
  ContentType,
  RemoteScormCommand,
  type RemoteScormMessage,
} from '@/models/Session';
import { LoginOrigin } from '@/models/User';
import { Activity, ActivityStatus } from '@/models/Activity';

import { computed, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { useI18n } from 'vue-i18n';

import {
  getActivityPlayList,
  toggleFavorite,
  getCourseProgress,
  getCourseMilestones,
} from '@/utils/course';

import { useSessionStore } from './useSessionStore';
import { useContentStore } from './useContentStore';
import { useWebSessionStore } from './useWebSessionStore';
import { useToastStore } from './useToastStore';
import { useActivityPlaylistStore } from './useActivityPlaylistStore';

export const useCourseStore = defineStore('course', () => {
  const { t: translate } = useI18n();

  const milestoneLogs = ref<Milestone[]>([]);
  const courseProgress = ref<CourseProgress[]>([]);
  const courseSummary = ref<Summary>();

  const isFavorited = computed(() =>
    milestoneLogs.value.some(({ logCode }) => logCode === MilestoneLogCode.courseFavorited),
  );
  const certificateLogs = computed(() =>
    milestoneLogs.value.filter(({ logCode }) =>
      [MilestoneLogCode.participationCertificate, MilestoneLogCode.testCertificate].includes(
        logCode,
      ),
    ),
  );

  const sessionStore = useSessionStore();
  const websessionStore = useWebSessionStore();
  const contentStore = useContentStore();
  const activityPlaylistStore = useActivityPlaylistStore();
  const toastStore = useToastStore();

  const courseContent = computed(() => contentStore.contentData as Course);

  const initializeCourse = () => {
    if (sessionStore.contentType !== ContentType.course) return;

    setActivityPlayList();
    setProgress();
    setMilestoneLogs();

    window.document.title = courseContent.value.name;

    window.addEventListener('beforeunload', handleRemoteScormFinish);
  };

  const setActivityPlayList = () => {
    if (!courseContent.value) return;

    const activityPlayList = getActivityPlayList(courseContent.value);
    courseContent.value.activityPlayList = activityPlayList;

    activityPlaylistStore.setPlaylist(activityPlayList);
  };

  const setCourseSummary = (activity: Activity) => {
    if (!courseContent.value) return;

    const targetProgress = courseProgress.value.find((p) => p.id === activity.id);
    if (targetProgress) {
      targetProgress.status = activity.status as ActivityStatus;
      targetProgress.progress = activity.progress as number;
      targetProgress.studiedDuration = activity.studiedDuration as number;
    }

    setMilestoneLogs();
  };

  const setFavorited = async () => {
    if (!sessionStore.contentId) return;

    try {
      const favoritedStatus = await toggleFavorite(sessionStore.contentId);

      websessionStore.updateWebSessionFavoritedCoursesList(sessionStore.contentId, favoritedStatus);

      await setMilestoneLogs();
    } catch (error) {
      console.error(error);
      toastStore.showToast({
        title: translate('toast.error'),
        message: translate('toast.cannot_favorite'),
      });
    }
  };

  const handleRemoteScormUpdateScore = (score: number, testScore: number | undefined) => {
    if (
      !sessionStore.platform ||
      sessionStore.platform.toLowerCase() !== LoginOrigin.RS.toLowerCase()
    )
      return;

    const remoteScormUpdateScoreMessage: RemoteScormMessage = {
      Command: RemoteScormCommand.updateScore,
      Score: score,
      TestScore: testScore ?? 0,
    };

    window.parent.postMessage(
      remoteScormUpdateScoreMessage,
      import.meta.env.VITE_LMS_CONNECTOR_API,
    );
  };

  const handleRemoteScormFinish = () => {
    if (
      !sessionStore.platform ||
      sessionStore.platform.toLowerCase() !== LoginOrigin.RS.toLowerCase()
    )
      return;

    const remoteScormFinishMessage: RemoteScormMessage = {
      Command: RemoteScormCommand.finish,
    };

    window.parent.postMessage(remoteScormFinishMessage, import.meta.env.VITE_LMS_CONNECTOR_API);
  };

  const setMilestoneLogs = async () => {
    if (!courseContent.value) return;

    try {
      milestoneLogs.value = await getCourseMilestones(sessionStore.contentId as number);
    } catch (error) {
      console.error(error);
    }
  };

  const setProgress = async () => {
    if (!courseContent.value) return;

    try {
      courseProgress.value = await getCourseProgress(sessionStore.contentId as number);
    } catch (error) {
      console.error(error);
    }
  };

  const updateActivities = () => {
    if (!courseProgress.value) return;

    courseProgress.value.forEach((activityProgress) => {
      const targetActivity =
        courseContent.value.certification?.activities.find((a) => a.id === activityProgress.id) ??
        courseContent.value.quickscan?.activities.find((a) => a.id === activityProgress.id) ??
        courseContent.value.modules
          .flatMap((module) => module.activities)
          .find((a) => a.id === activityProgress.id);

      if (targetActivity) {
        targetActivity.status = activityProgress.status as ActivityStatus;
        targetActivity.progress = activityProgress.progress;
        targetActivity.studiedDuration = activityProgress.studiedDuration;
      }
    });
  };

  const setSummary = () => {
    if (!courseProgress.value) return;

    const testActivity = courseProgress.value.find((a) => a.nodeTypeId === 40055);

    if (sessionStore.contentMode === ContentMode.test) {
      const totalStudiedDurationInSeconds = testActivity?.studiedDuration ?? 0;

      courseSummary.value = {
        moduleCount: 0,
        activityCount: 0,
        completionPercent: 0,
        testCompletionPercent: Math.round(testActivity?.progress ?? 0),
        testStatus: testActivity?.status as ActivityStatus,
        studiedDuration:
          totalStudiedDurationInSeconds === 0
            ? 0
            : Math.round(totalStudiedDurationInSeconds / 60) ?? 1,
      };

      return;
    }

    const allActivities = courseProgress.value.filter((a) => a.nodeTypeId !== 40055);
    const activitiesCompletionPercent =
      allActivities.length > 0
        ? Math.round(
            allActivities.reduce((acc, activity) => acc + (activity.progress ?? 0), 0) /
              allActivities.length,
          )
        : 0;

    if (sessionStore.contentMode === ContentMode.content) {
      const totalStudiedDurationInSeconds = allActivities.reduce(
        (acc, activity) => acc + (activity.studiedDuration ?? 0),
        0,
      );

      courseSummary.value = {
        moduleCount: courseContent.value.modules.length,
        activityCount: allActivities.length,
        completionPercent: activitiesCompletionPercent > 100 ? 100 : activitiesCompletionPercent,
        testCompletionPercent: 0,
        testStatus: ActivityStatus.notAttempted,
        studiedDuration:
          totalStudiedDurationInSeconds === 0
            ? 0
            : Math.round(totalStudiedDurationInSeconds / 60) ?? 1,
      };

      return;
    }

    const totalStudiedDurationInSeconds = courseProgress.value.reduce(
      (acc, activity) => acc + (activity.studiedDuration ?? 0),
      0,
    );

    courseSummary.value = {
      moduleCount: courseContent.value.modules.length,
      activityCount: allActivities.length,
      completionPercent: activitiesCompletionPercent > 100 ? 100 : activitiesCompletionPercent,
      testCompletionPercent: testActivity?.progress ?? 0,
      testStatus: testActivity?.status as ActivityStatus,
      studiedDuration:
        totalStudiedDurationInSeconds === 0
          ? 0
          : Math.round(totalStudiedDurationInSeconds / 60) ?? 1,
    };
  };

  watch(
    () => courseContent.value,
    () => initializeCourse(),
  );

  watch(
    courseProgress,
    () => {
      updateActivities();
      setSummary();
    },
    { deep: true },
  );

  watch(
    courseSummary,
    (summary) => {
      if (!summary) return;

      if (summary.completionPercent === 100) {
        websessionStore.updateWebsessionRunningCoursesList(sessionStore.contentId as number, true);
      }

      if (sessionStore.activityMode === ActivityMode.normal) {
        handleRemoteScormUpdateScore(
          summary.completionPercent,
          summary.testStatus === ActivityStatus.passed ? 100 : 0,
        );
      }
    },
    { deep: true },
  );

  return {
    courseContent,
    isFavorited,
    courseSummary,
    certificateLogs,
    setFavorited,
    setCourseSummary,
    handleRemoteScormUpdateScore,
  };
});
